import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { AppFeatureActions } from '@app/app.actions';
import {
  getExhibitionSettings,
  getSelectedExhibitionId,
  getSelfRegistration,
  selectIsTicketAssignmentStepVisible
} from '@app/app.reducer';
import { DynamicQuestionnaireFields } from '@core/data-access/models/api/dynamic-questionnaire-fields.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { InitializationService } from '@core/data-access/models/service.model';
import { QuestionnaireApiService } from '@core/data-access/services/api/questionnaire-api.service';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { SavedTicketHolderFormsFactory } from '@core/features/account/features/saved-ticket-holders/services/saved-ticket-holder-forms-factory';
import * as savedTicketHolderEffects from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.effects';
import {
  initialSavedTicketsHolderState,
  savedTicketHoldersFeatureKey,
  savedTicketHoldersReducer
} from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.reducer';
import { BuyerQuestionnaireFormFactory } from '@core/features/buyer-questionnaire/services/buyer-questionnaire-form-factory.service';
import * as buyerQuestionnaireEffects from '@core/features/buyer-questionnaire/store/buyer-questionnaire.effects';
import {
  buyerQuestionnaireReducer,
  buyerQuestionnaireReducerKey,
  initialBuyerQuestionnaireStoreState
} from '@core/features/buyer-questionnaire/store/buyer-questionnaire.reducer';
import { selectBuyerQuestionnaireDynamicFields } from '@core/features/buyer-questionnaire/store/buyer-questionnaire.selectors';
import { TicketHolderFormsFactory } from '@core/features/ticket-assignment/services/ticket-holder-forms-factory.service';
import { TicketHolderQuestionnaireFormsFactory } from '@core/features/ticket-assignment/services/ticket-holder-questionnaire-forms-factory.service';
import { TicketAssignmentActions } from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import * as ticketAssignmentEffects from '@core/features/ticket-assignment/store/ticket-assignment.effects';
import {
  initialTicketAssignmentStoreState,
  ticketAssignmentReducer,
  ticketAssignmentReducerKey
} from '@core/features/ticket-assignment/store/ticket-assignment.reducer';
import { EffectSources } from '@ngrx/effects';
import { combineReducers, ReducerManager, select, Store } from '@ngrx/store';
import { StoreFeature } from '@ngrx/store/src/models';
import { isAnonymousProductBooked } from '@store/products/booking/booking.selectors';
import { isEmpty, isNil, isNull } from 'lodash';
import { catchError, combineLatest, filter, iif, map, of, pairwise, startWith, switchMap, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FeatureManager implements InitializationService {
  readonly #injector = inject(Injector);
  readonly #effectsSources = inject(EffectSources);
  readonly #reducerManager = inject(ReducerManager);
  readonly #store = inject(Store);
  readonly #formMediator = inject(FormMediator);
  readonly #questionnaireApiService = inject(QuestionnaireApiService);

  init() {
    this.initLoadBuyerQuestionnaireFeature();
    this.initLoadTicketAssignmentFeature();
  }

  addSavedTicketHolderFeature() {
    this.#formMediator.addFormService(FormName.SavedTicketHolders, SavedTicketHolderFormsFactory);

    this.addStoreFeature(
      {
        key: savedTicketHoldersFeatureKey,
        reducers: savedTicketHoldersReducer,
        initialState: initialSavedTicketsHolderState,
        reducerFactory: combineReducers,
        metaReducers: []
      },
      savedTicketHolderEffects
    );

    this.#store.dispatch(AppFeatureActions.savedTicketHolderFeatureAdded());
  }

  removeSavedTicketHolderFeature() {
    this.#formMediator.removeFormService(FormName.SavedTicketHolders);

    this.removeStoreFeature({
      key: savedTicketHoldersFeatureKey,
      reducers: savedTicketHoldersReducer,
      initialState: initialSavedTicketsHolderState,
      reducerFactory: combineReducers,
      metaReducers: []
    });

    this.#store.dispatch(AppFeatureActions.savedTicketHolderFeatureRemoved());
  }

  private initLoadBuyerQuestionnaireFeature() {
    return this.addOrRemoveFeature$()
      .pipe(
        withLatestFrom(
          this.#store.select(getSelfRegistration),
          this.#store.select(selectBuyerQuestionnaireDynamicFields)
        ),
        switchMap(
          ([
            { selectedExhibitionId, isExhibition, isHomePage, isBackToHomePage },
            isSelfRegistration,
            buyerQuestionnaireDynamicFields
          ]) =>
            iif(
              () => !isEmpty(buyerQuestionnaireDynamicFields),
              of(buyerQuestionnaireDynamicFields),
              this.#questionnaireApiService.getBuyerQuestionnaire$([], selectedExhibitionId, isSelfRegistration)
            ).pipe(
              map((dynamicQuestionnaireFields) => ({
                addFeature: isExhibition && !(isHomePage || isBackToHomePage) && !isEmpty(dynamicQuestionnaireFields),
                dynamicQuestionnaireFields
              }))
            )
        ),
        catchError(() => of({ addFeature: false, dynamicQuestionnaireFields: [] }))
      )
      .subscribe(({ addFeature, dynamicQuestionnaireFields }) => {
        addFeature
          ? this.addBuyerQuestionnaireFeature(dynamicQuestionnaireFields)
          : this.removeBuyerQuestionnaireFeature();
      });
  }

  private initLoadTicketAssignmentFeature() {
    const waitForTicketAssignmentStateResetToSetStepVisibility$ = this.#store.pipe(
      select(selectIsTicketAssignmentStepVisible),
      filter((selectIsTicketAssignmentStepVisible) => !isNull(selectIsTicketAssignmentStepVisible))
    );

    return combineLatest({
      isTicketAssignmentStepVisible: waitForTicketAssignmentStateResetToSetStepVisibility$,
      addOrRemoveFeature: this.addOrRemoveFeature$()
    })
      .pipe(
        withLatestFrom(this.#store.select(isAnonymousProductBooked)),
        filter(([_, isAnonymousProductBooked]) => !isAnonymousProductBooked),
        map(
          ([
            {
              isTicketAssignmentStepVisible,
              addOrRemoveFeature: { isHomePage, isBackToHomePage, isExhibition }
            }
          ]) => ({
            addFeature: isExhibition && isTicketAssignmentStepVisible && !(isHomePage || isBackToHomePage),
            isExhibition
          })
        )
      )
      .subscribe(({ addFeature, isExhibition }) =>
        addFeature ? this.addTicketAssignmentFeature() : this.removeTicketAssignmentFeature(isExhibition)
      );
  }

  private addOrRemoveFeature$() {
    return combineLatest({
      selectedExhibitionId: this.#store.pipe(select(getSelectedExhibitionId), startWith(null)),
      exhibitionSettings: this.#store.pipe(select(getExhibitionSettings), startWith(null))
    }).pipe(
      pairwise(),
      map(([previousChanges, currentChanges]) => {
        const isPreviousExhibitionIdNotSet = isNil(previousChanges.selectedExhibitionId);
        const isPreviousExhibitionSettingsNotSet = isNil(previousChanges.exhibitionSettings);
        const isCurrentExhibitionIdNotSet = isNil(currentChanges.selectedExhibitionId);
        const isCurrentExhibitionSettingsNotSet = isNil(currentChanges.exhibitionSettings);

        const isHomePage =
          isPreviousExhibitionIdNotSet &&
          isPreviousExhibitionSettingsNotSet &&
          isCurrentExhibitionIdNotSet &&
          isCurrentExhibitionSettingsNotSet;

        const isBackToHomePage =
          !isPreviousExhibitionIdNotSet &&
          isPreviousExhibitionSettingsNotSet &&
          isCurrentExhibitionIdNotSet &&
          isCurrentExhibitionSettingsNotSet;

        const isExhibition =
          ((!isPreviousExhibitionIdNotSet && isPreviousExhibitionSettingsNotSet) ||
            (isPreviousExhibitionIdNotSet && !isPreviousExhibitionSettingsNotSet)) &&
          !isCurrentExhibitionIdNotSet &&
          !isCurrentExhibitionSettingsNotSet;

        return {
          selectedExhibitionId: currentChanges.selectedExhibitionId,
          exhibitionSettings: currentChanges.exhibitionSettings,
          isHomePage,
          isBackToHomePage,
          isExhibition
        };
      }),
      filter(({ isHomePage, isBackToHomePage, isExhibition }) => isHomePage || isBackToHomePage || isExhibition)
    );
  }

  private addBuyerQuestionnaireFeature(dynamicQuestionnaireFields: DynamicQuestionnaireFields) {
    this.#formMediator.addFormService(FormName.BuyerQuestionnaire, BuyerQuestionnaireFormFactory);

    this.addStoreFeature(
      {
        key: buyerQuestionnaireReducerKey,
        reducers: buyerQuestionnaireReducer,
        initialState: initialBuyerQuestionnaireStoreState,
        reducerFactory: combineReducers,
        metaReducers: []
      },
      buyerQuestionnaireEffects
    );

    this.#store.dispatch(AppFeatureActions.buyerQuestionnaireFeatureAdded(dynamicQuestionnaireFields));
  }

  private removeBuyerQuestionnaireFeature() {
    this.#formMediator.removeFormService(FormName.BuyerQuestionnaire);

    this.removeStoreFeature({
      key: buyerQuestionnaireReducerKey,
      reducers: buyerQuestionnaireReducer,
      initialState: initialBuyerQuestionnaireStoreState,
      reducerFactory: combineReducers,
      metaReducers: []
    });

    this.#store.dispatch(AppFeatureActions.buyerQuestionnaireFeatureRemoved());
  }

  private addTicketAssignmentFeature() {
    this.#formMediator.addFormService(FormName.TicketHolder, TicketHolderFormsFactory);
    this.#formMediator.addFormService(FormName.TicketHolderQuestionnaire, TicketHolderQuestionnaireFormsFactory);

    this.addStoreFeature(
      {
        key: ticketAssignmentReducerKey,
        reducers: ticketAssignmentReducer,
        initialState: initialTicketAssignmentStoreState,
        reducerFactory: combineReducers,
        metaReducers: []
      },
      ticketAssignmentEffects
    );

    this.#store.dispatch(AppFeatureActions.ticketAssignmentFeatureAdded());
  }

  private removeTicketAssignmentFeature(isExhibition: boolean, resetReducer = false) {
    this.#store.dispatch(AppFeatureActions.ticketAssignmentFeatureRemoved());

    if (resetReducer) {
      this.#store.dispatch(TicketAssignmentActions.resetReducer());
    }

    if (isExhibition) {
      return;
    }

    this.#formMediator.removeFormService(FormName.TicketHolder);
    this.#formMediator.removeFormService(FormName.TicketHolderQuestionnaire);

    this.removeStoreFeature({
      key: ticketAssignmentReducerKey,
      reducers: ticketAssignmentReducer,
      initialState: initialTicketAssignmentStoreState,
      reducerFactory: combineReducers,
      metaReducers: []
    });
  }

  private addStoreFeature(feature: StoreFeature<any, any>, effectSourceInstance: any) {
    this.#reducerManager.addFeature(feature);

    runInInjectionContext(this.#injector, () => {
      this.#effectsSources.addEffects(effectSourceInstance);
    });
  }

  private removeStoreFeature(reducer: StoreFeature<any, any>) {
    this.#reducerManager.removeFeature(reducer);
  }
}
